import { useCallback, useEffect, useRef, useState } from 'react';
import { useTransientLoadingState } from '@/features/shared/hooks/useTransientLoadingState';
import type { DailyHistorySortKey } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';

type UseDailyHistoryPanelStateArgs = {
  defaultSortKey: DailyHistorySortKey;
};

type UseDailyHistoryPanelStateResult = {
  page: number;
  pageSize: number;
  sortKey: DailyHistorySortKey;
  sortDirection: 'asc' | 'desc';
  isTransitionLoading: boolean;
  setPage: (nextPage: number) => void;
  setPageSize: (nextPageSize: number) => void;
  setSortKey: (nextSortKey: DailyHistorySortKey) => void;
  setSortDirection: (nextSortDirection: 'asc' | 'desc') => void;
};

export const useDailyHistoryPanelPageReset = ({
  resetKey,
  setPage,
}: {
  resetKey: string;
  setPage: (nextPage: number) => void;
}): void => {
  useEffect(() => {
    setPage(0);
  }, [resetKey, setPage]);
};

export const useDailyHistoryPanelState = ({
  defaultSortKey,
}: UseDailyHistoryPanelStateArgs): UseDailyHistoryPanelStateResult => {
  const [page, setPageState] = useState(0);
  const [pageSize, setPageSizeState] = useState(5);
  const [sortKey, setSortKeyState] = useState<DailyHistorySortKey>(defaultSortKey);
  const [sortDirection, setSortDirectionState] = useState<'asc' | 'desc'>('asc');
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  const sortKeyRef = useRef(sortKey);
  const sortDirectionRef = useRef(sortDirection);
  const { isLoading, triggerLoading } = useTransientLoadingState();
  const setPage = useCallback(
    (nextPage: number): void => {
      if (pageRef.current === nextPage) {
        return;
      }
      pageRef.current = nextPage;
      triggerLoading();
      setPageState(nextPage);
    },
    [triggerLoading],
  );
  const setPageSize = useCallback(
    (nextPageSize: number): void => {
      if (pageSizeRef.current === nextPageSize) {
        return;
      }
      pageSizeRef.current = nextPageSize;
      triggerLoading();
      setPageSizeState(nextPageSize);
    },
    [triggerLoading],
  );
  const setSortKey = useCallback(
    (nextSortKey: DailyHistorySortKey): void => {
      if (sortKeyRef.current === nextSortKey) {
        return;
      }
      sortKeyRef.current = nextSortKey;
      triggerLoading();
      setSortKeyState(nextSortKey);
    },
    [triggerLoading],
  );
  const setSortDirection = useCallback(
    (nextSortDirection: 'asc' | 'desc'): void => {
      if (sortDirectionRef.current === nextSortDirection) {
        return;
      }
      sortDirectionRef.current = nextSortDirection;
      triggerLoading();
      setSortDirectionState(nextSortDirection);
    },
    [triggerLoading],
  );

  return {
    page,
    pageSize,
    sortKey,
    sortDirection,
    isTransitionLoading: isLoading,
    setPage,
    setPageSize,
    setSortKey,
    setSortDirection,
  };
};
